import React, { useState } from 'react';
import axios from 'axios';
import translations from './translations';
import './ResultSubmission.css';
import { getTimeTakenSafe } from '../utils/timing';

function ResultSubmission() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [evaluationUuid, setEvaluationUuid] = useState('');
  const [copyStatus, setCopyStatus] = useState('');

  const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};
  const lang = ['zh-TW', 'zh-CN', 'vi', 'en'].includes(userInfo.defaultLang)
    ? userInfo.defaultLang
    : 'zh-TW';

  const tRoot = translations[lang] || translations['zh-TW'];
  const t = tRoot.resultSubmission;
  const errorMap = tRoot.errorMessages || {};

  const submitResults = async () => {
    if (submitted || loading) return;

    setLoading(true);
    setMessage('');
    setEvaluationUuid('');
    setCopyStatus('');

    const accessToken = process.env.REACT_APP_TPES_API_TOKEN;
    const quizAnswers = JSON.parse(localStorage.getItem('quizAnswers')) || {};
    const timeTaken = getTimeTakenSafe();

    const { defaultLang, useruuid, evaluation_uuid, name, email, phone, birthYear, gender } = userInfo;

    // 从当前URL获取参数，确保只使用当前链接中的参数
    const urlParams = new URLSearchParams(window.location.search);
    const companyCode = urlParams.get('companyCode');
    const jobNID = urlParams.get('Job_NID');
    const codeType = urlParams.get('codeType');
    const evaluationType = urlParams.get('evaluation_type');
    
    // 调试日志：检查URL参数获取情况
    console.log('=== ResultSubmission URL参数调试 ===');
    console.log('完整URL:', window.location.href);
    console.log('查询字符串:', window.location.search);
    console.log('获取到的参数:');
    console.log('  companyCode:', companyCode);
    console.log('  Job_NID:', jobNID);
    console.log('  codeType:', codeType);
    console.log('  evaluation_type:', evaluationType);

    const e_data = {
      time: timeTaken,
      defaultLang,
      useruuid,
      evaluation_uuid,
    };
    
    // 只有当URL中存在这些参数时才添加到 e_data 中
    if (companyCode) {
      e_data.companyCode = companyCode;
      console.log('✅ ResultSubmission 添加 companyCode:', companyCode);
    }
    if (jobNID) {
      e_data.Job_NID = jobNID;
      console.log('✅ ResultSubmission 添加 Job_NID:', jobNID);
    } else {
      console.log('❌ ResultSubmission Job_NID 为空或不存在，未添加到 e_data');
    }
    if (codeType) {
      e_data.codeType = codeType;
      console.log('✅ ResultSubmission 添加 codeType:', codeType);
    }
    if (evaluationType) {
      e_data.evaluation_type = evaluationType;
      console.log('✅ ResultSubmission 添加 evaluation_type:', evaluationType);
    }
    
    console.log('ResultSubmission 最终的 e_data:', JSON.stringify(e_data, null, 2));
    
    // 如果用户信息中包含基本信息（通过表单填写的），则添加到e_data中
    if (name) {
      e_data.username = name;
    }
    if (email) {
      e_data.useremail = email;
    }
    if (phone) {
      e_data.userphone = phone;
    }
    if (birthYear !== undefined) {
      e_data.year = birthYear;
    }
    if (gender !== undefined) {
      e_data.usersex = gender;
    }

    const e_content = Object.entries(quizAnswers).map(([no, fraction]) => ({
      no: parseInt(no, 10),
      fraction: parseInt(fraction, 10),
    }));

    const createdAt = Math.floor(Date.now() / 1000);

    const postData = {
      outrep: 'career',
      e_data,
      e_content,
      created_at: createdAt,
    };

    // const apiurl = 'http://localhost:7000/api/assessment/evaluation';
    const apiurl = `${process.env.REACT_APP_TPES_API_BASE_URL}/api/assessment/evaluation`;
    // const apiurl = 'https://tpesapi.properson.net/api/assessment/evaluation';

    try {
      const response = await axios.post(apiurl, postData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        setMessage(`✅ ${t.success}`);
        setSubmitted(true);
        setEvaluationUuid(response.data.evaluation_uuid || '');
        
        // 清理测评相关的localStorage数据
        localStorage.removeItem('startTime');
        localStorage.removeItem('timeTaken');
        localStorage.removeItem('quizAnswers');
        const orderKey = `quizOrder_${defaultLang || lang}`;
        localStorage.removeItem(orderKey);
        
        // 将用户标记为已完成测评
        const completedEvaluations = JSON.parse(localStorage.getItem('completedEvaluations') || '[]');
        if (!completedEvaluations.includes(useruuid)) {
          completedEvaluations.push(useruuid);
          localStorage.setItem('completedEvaluations', JSON.stringify(completedEvaluations));
        }
        
        // 清除该用户的未完成会话记录
        const existingSessions = localStorage.getItem('userEvaluationSessions');
        if (existingSessions) {
          const sessions = JSON.parse(existingSessions);
          if (sessions[useruuid]) {
            delete sessions[useruuid];
            localStorage.setItem('userEvaluationSessions', JSON.stringify(sessions));
          }
        }
      } else {
        setMessage(`❌ ${t.fail} (status: ${response.status})`);
      }
    } catch (error) {
      const errorCode = error.response?.data?.error_code;
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message;

      const translatedError =
        errorCode && errorMap[errorCode]
          ? errorMap[errorCode]
          : backendMessage;

      setMessage(`❌ ${t.fail}：${translatedError}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (evaluationUuid) {
      navigator.clipboard.writeText(evaluationUuid).then(() => {
        setCopyStatus(t.copied);
        setTimeout(() => setCopyStatus(''), 2000);
      });
    }
  };

  return (
    <div className="result-submission-container">
      <div className="result-submission-card">
        <h1>{t.title}</h1>
        <button
          onClick={submitResults}
          disabled={submitted || loading}
          className="submit-button"
        >
          {loading ? t.buttonLoading : t.button}
        </button>

        {message && <div className="submission-message">{message}</div>}

        {evaluationUuid && (
          <div className="submission-message">
            {t.evaluationCode}：<strong>{evaluationUuid}</strong>{' '}
            <button onClick={handleCopy} style={{ marginLeft: '10px' }}>
              {t.copy}
            </button>
            {copyStatus && (
              <span style={{ marginLeft: '8px', color: 'green' }}>
                {copyStatus}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ResultSubmission;
